import { useEffect } from "react";
import { Link } from "react-router-dom";
import Footer from "../components/Footer";

const Section = ({ children, className = "", dark = false }) => (
  <section className={`py-16 sm:py-24 px-6 ${className}`}>
    <div className="max-w-2xl mx-auto text-center space-y-6">
      {children}
    </div>
  </section>
);

const SectionTitle = ({ subtitle, title, dark = false }) => (
  <div className="space-y-4 mb-4">
    {subtitle && (
      <p className={`text-[10px] sm:text-xs tracking-[0.3em] uppercase ${dark ? 'text-[#C5A059]' : 'text-[#C5A059]'}`}>
        {subtitle}
      </p>
    )}
    <div className={`w-12 h-[1px] mx-auto ${dark ? 'bg-[#C5A059]/50' : 'bg-[#C5A059]/40'}`} />
    <h2 
      className={`font-serif text-xl sm:text-2xl md:text-3xl ${dark ? 'text-[#E8D2B5]' : 'text-[#3F2A24]'}`}
      style={{ textWrap: 'balance' }}
    >
      {title}
    </h2>
  </div>
);

const Paragraph = ({ children, dark = false, italic = false }) => (
  <p 
    className={`text-sm sm:text-base leading-[2] whitespace-pre-line ${dark ? 'text-[#E8D2B5]/85' : 'text-[#3F2A24]/80'} ${italic ? 'font-serif italic' : ''}`}
    style={{ textWrap: 'balance' }}
  >
    {children}
  </p>
);

const PourquoiCoconTransforme = () => {
  useEffect(() => {
    document.title = "Pourquoi COCON transforme vraiment | COCON";
    let metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", "La transformation ne vient pas d'une technique. Elle vient du cadre, du lien, de la présence. Découvrez pourquoi COCON transforme vraiment.");
    }
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#E8D2B5]/50 text-[#3F2A24]">
      
      {/* Back navigation */}
      <div className="fixed top-6 left-6 z-50">
        <Link
          to="/valouse"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#3F2A24]/10 hover:bg-[#3F2A24]/20 backdrop-blur-sm transition-colors text-[#3F2A24] text-sm"
          data-testid="back-from-pourquoi"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          Retour
        </Link>
      </div>

      {/* HERO */}
      <section className="pt-24 pb-16 sm:pt-32 sm:pb-24 px-6 bg-[#ecd9c1]">
        <div className="max-w-2xl mx-auto text-center space-y-6">
          <p className="text-[10px] sm:text-xs tracking-[0.3em] uppercase text-[#C5A059]">
            COCON
          </p>
          <h1 
            className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#3F2A24] tracking-wide"
            style={{ textWrap: 'balance' }}
          >
            Pourquoi COCON transforme vraiment
          </h1>
          <div className="w-16 h-[1px] bg-[#C5A059]/50 mx-auto" />
        </div>
      </section>

      {/* INTRODUCTION */}
      <Section className="bg-[#E8D2B5]/50">
        <Paragraph>
          {"Certaines expériences apportent des réponses."}
        </Paragraph>
        <Paragraph italic>
          {"COCON apporte un espace."}
        </Paragraph>
        <div className="w-8 h-[1px] bg-[#C5A059]/30 mx-auto" />
        <Paragraph>
          {"Un espace tenu.\nUn espace où quelque chose peut réellement bouger."}
        </Paragraph>
        <Paragraph italic>
          {"Pas dans l'idée.\nDans le corps."}
        </Paragraph>
      </Section>

      {/* CE QUI TRANSFORME ICI */}
      <Section className="bg-[#d9cfc4]">
        <SectionTitle title="Ce qui transforme ici" />
        <Paragraph>
          {"La transformation ne vient pas d'une technique."}
        </Paragraph>
        <Paragraph>
          {"Elle vient du cadre.\nDu lien.\nDe la présence."}
        </Paragraph>
        <Paragraph italic>
          {"Du fait d'être vu.\nEt de pouvoir déposer l'armure."}
        </Paragraph>
      </Section>

      {/* UN ESPACE TENU */}
      <Section className="bg-[#3F2A24]">
        <SectionTitle title="Un espace tenu" dark />
        <Paragraph dark>
          {"COCON est construit comme un contenant."}
        </Paragraph>
        <Paragraph dark>
          {"Un cercle limité.\nUn lieu entièrement privatisé.\nUn rythme précis."}
        </Paragraph>
        <Paragraph dark italic>
          {"Avant.\nPendant.\nAprès."}
        </Paragraph>
        <div className="w-8 h-[1px] bg-[#C5A059]/30 mx-auto" />
        <Paragraph dark>
          {"Ce cadre permet quelque chose de rare\u00A0:"}
        </Paragraph>
        <p className="font-serif text-lg sm:text-xl text-[#C5A059] italic">
          ne plus se protéger.
        </p>
      </Section>

      {/* PAS DE SPECTATEURS */}
      <Section className="bg-[#E8D2B5]/50">
        <SectionTitle title="Pas de spectateurs" />
        <Paragraph>
          {"Dans COCON, personne ne regarde."}
        </Paragraph>
        <Paragraph italic>
          {"Tout le monde traverse."}
        </Paragraph>
        <div className="w-8 h-[1px] bg-[#C5A059]/30 mx-auto" />
        <Paragraph>
          {"Tu ne viens pas consommer une expérience."}
        </Paragraph>
        <Paragraph italic>
          {"Tu viens rencontrer ce qui est vivant en toi."}
        </Paragraph>
      </Section>

      {/* DES TRANSFORMATIONS RÉELLES */}
      <Section className="bg-[#516258]">
        <SectionTitle title="Des transformations réelles" dark />
        <Paragraph dark>
          {"Ce qui change n'est pas spectaculaire."}
        </Paragraph>
        <Paragraph dark italic>
          {"Mais c'est tangible."}
        </Paragraph>
        <div className="w-8 h-[1px] bg-[#C5A059]/30 mx-auto" />
        <Paragraph dark>
          {"Tu respires autrement.\nTu te sens vu.\nTu cesses de lutter contre toi-même."}
        </Paragraph>
        <Paragraph dark italic>
          {"Tu repars aligné."}
        </Paragraph>
        <div className="w-8 h-[1px] bg-[#C5A059]/20 mx-auto" />
        <Paragraph dark>
          {"Et parfois,\nce qui se transforme ici\ncontinue à agir longtemps après."}
        </Paragraph>
      </Section>

      {/* CTA */}
      <section className="py-16 sm:py-20 px-6 bg-[#ecd9c1]">
        <div className="max-w-2xl mx-auto text-center space-y-8">
          <div className="w-16 h-[1px] bg-[#C5A059]/40 mx-auto" />
          <Link
            to="/valouse"
            className="inline-block px-8 py-4 bg-[#3F2A24] text-white rounded-full uppercase tracking-[0.2em] text-xs hover:bg-[#56433e] transition-colors min-h-[48px]"
          >
            Découvrir COCON Valouse
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PourquoiCoconTransforme;
